import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';



/**
 * SearchMessages Component
 * Search within conversation messages
 *
 * @param {Object} props
 * @param {Array} props.messages - All messages to search through
 * @param {function} props.onClose - Handler to close search
 * @param {function} props.onResultClick - Handler when a result is clicked
 */
const SearchMessages = ({ messages, onClose, onResultClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const searchInputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Search messages
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setCurrentIndex(-1);
      return;
    }

    const query = searchQuery.toLowerCase();
    const found = messages.filter(msg =>
      !msg.deleted_at && msg.body && msg.body.toLowerCase().includes(query)
    );

    setResults(found);
    setCurrentIndex(found.length > 0 ? 0 : -1);
  }, [searchQuery, messages]);

  // Navigate to result
  useEffect(() => {
    if (currentIndex >= 0 && results[currentIndex]) {
      onResultClick?.(results[currentIndex]);
    }
  }, [currentIndex, results, onResultClick]);

  const handlePrevious = () => {
    if (results.length === 0) return;
    setCurrentIndex(prev => prev > 0 ? prev - 1 : results.length - 1);
  };

  const handleNext = () => {
    if (results.length === 0) return;
    setCurrentIndex(prev => prev < results.length - 1 ? prev + 1 : 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.shiftKey ? handlePrevious() : handleNext();
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  return (
    <div className="talk-search-messages">
      <div className="talk-search-messages-input-container">
        <FiSearch size={18} className="talk-search-messages-icon" />
        <input
          ref={searchInputRef}
          type="text"
          className="talk-search-messages-input"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {searchQuery && (
          <div className="talk-search-messages-counter">
            {results.length > 0
              ? `${currentIndex + 1} of ${results.length}`
              : 'No results'
            }
          </div>
        )}

        <div className="talk-search-messages-actions">
          <button
            className="talk-search-messages-btn"
            onClick={handlePrevious}
            disabled={results.length === 0}
            aria-label="Previous result"
            title="Previous (Shift+Enter)"
          >
            <FiChevronUp size={18} />
          </button>

          <button
            className="talk-search-messages-btn"
            onClick={handleNext}
            disabled={results.length === 0}
            aria-label="Next result"
            title="Next (Enter)"
          >
            <FiChevronDown size={18} />
          </button>

          <button
            className="talk-search-messages-btn"
            onClick={onClose}
            aria-label="Close search"
            title="Close (Esc)"
          >
            <FiX size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchMessages;
